/**
 * UserShow - Vue détaillée d'un utilisateur avec onglets
 */

import React, { useState, useEffect } from 'react';
import { PUBLIC_APP_URL } from '../../config';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Title, useNotify } from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Button,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
} from '@mui/material';

// Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VerifiedIcon from '@mui/icons-material/Verified';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import BarChartIcon from '@mui/icons-material/BarChart';
import PersonIcon from '@mui/icons-material/Person';
import StarIcon from '@mui/icons-material/Star';
import EmailIcon from '@mui/icons-material/Email';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Queries & Mutations
import {
  GET_ADMIN_USER,
  GET_ADMIN_USER_CARDS,
  GET_ADMIN_USER_STATS,
  GET_ADMIN_USER_SUBSCRIPTION_HISTORY,
  SUSPEND_USER,
  REACTIVATE_USER,
  SET_VERIFIED_BADGE,
  SET_USER_ROLE,
  UPDATE_USER,
} from './user.queries';

// Types
interface AdminUser {
  id: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  displayName: string | null;
  username: string | null;
  slug: string | null;
  avatarUrl: string | null;
  jobTitle: string | null;
  company: string | null;
  bio: string | null;
  city: string | null;
  country: string | null;
  sector: string | null;
  profileViews: number;
  verifiedBadge: boolean;
  isPremium: boolean;
  accountStatus: 'ACTIVE' | 'SUSPENDED' | 'DELETED' | 'PENDING';
  role: 'USER' | 'ADMIN' | 'MODERATOR';
  createdAt: string;
  updatedAt: string;
  subscription: { plan: string; status: string } | null;
  cardsCount: number;
}

interface UserCard {
  id: string;
  slug: string;
  cardType: string;
  displayName: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
}

interface UserStats {
  profileViews: number;
  totalCards: number;
  totalConnections: number;
  accountAge: number;
  lastUpdated: string;
}

// Status colors
const STATUS_COLORS: Record<string, string> = {
  ACTIVE: '#10B981',
  SUSPENDED: '#EF4444',
  DELETED: '#6B7280',
  PENDING: '#F59E0B',
};

const CARD_TYPE_LABELS: Record<string, string> = {
  presentation: 'Carte Pro',
  business: 'Visite Guidée',
  evenement: 'Événement',
};

// Tab Panel
function TabPanel({ children, value, index }: { children: React.ReactNode; value: number; index: number }) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
      {value === index && children}
    </Box>
  );
}

// Info Item Component
function InfoItem({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
      <Box sx={{ color: 'text.secondary', mt: 0.5 }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2">{value || '-'}</Typography>
      </Box>
    </Box>
  );
}

// Stats Card
function StatsCard({ title, value, icon, color }: { title: string; value: number | string; icon: React.ReactNode; color: string }) {
  return (
    <Card>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2 }}>
        <Box sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          bgcolor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
        }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={600}>{value}</Typography>
          <Typography variant="caption" color="text.secondary">{title}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export function UserShow() {
  const { id: userId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const notify = useNotify();

  // State
  const [tabValue, setTabValue] = useState(0);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [suspendDialogOpen, setSuspendDialogOpen] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    jobTitle: '',
    company: '',
    city: '',
    country: '',
    bio: '',
  });

  // Queries
  const { data: userData, loading: userLoading, error: userError, refetch: refetchUser } = useQuery(GET_ADMIN_USER, {
    variables: { userId },
    skip: !userId,
  });

  useEffect(() => {
    if (userData?.adminUser) {
      setEditForm({
        firstName: userData.adminUser.firstName || '',
        lastName: userData.adminUser.lastName || '',
        displayName: userData.adminUser.displayName || '',
        email: userData.adminUser.email || '',
        jobTitle: userData.adminUser.jobTitle || '',
        company: userData.adminUser.company || '',
        city: userData.adminUser.city || '',
        country: userData.adminUser.country || '',
        bio: userData.adminUser.bio || '',
      });
    }
  }, [userData]);

  const { data: cardsData, loading: cardsLoading } = useQuery(GET_ADMIN_USER_CARDS, {
    variables: { userId },
    skip: !userId,
  });

  const { data: statsData, loading: statsLoading } = useQuery(GET_ADMIN_USER_STATS, {
    variables: { userId },
    skip: !userId,
  });

  const { data: _subscriptionData, loading: subscriptionLoading } = useQuery(GET_ADMIN_USER_SUBSCRIPTION_HISTORY, {
    variables: { userId },
    skip: !userId,
  });

  // Mutations
  const [suspendUser, { loading: suspending }] = useMutation(SUSPEND_USER);
  const [reactivateUser, { loading: reactivating }] = useMutation(REACTIVATE_USER);
  const [setVerifiedBadge, { loading: settingBadge }] = useMutation(SET_VERIFIED_BADGE);
  const [setUserRole, { loading: settingRole }] = useMutation(SET_USER_ROLE);
  const [updateUser, { loading: updating }] = useMutation(UPDATE_USER);

  const user: AdminUser | null = userData?.adminUser;
  const cards: UserCard[] = cardsData?.adminUserCards || [];
  const stats: UserStats | null = statsData?.adminUserStats;

  // Handlers
  const handleSuspend = async () => {
    try {
      await suspendUser({ variables: { userId, reason: suspendReason } });
      notify('Utilisateur suspendu', { type: 'success' });
      setSuspendDialogOpen(false);
      setSuspendReason('');
      refetchUser();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const handleReactivate = async () => {
    try {
      await reactivateUser({ variables: { userId } });
      notify('Utilisateur réactivé', { type: 'success' });
      refetchUser();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const handleToggleVerified = async () => {
    if (!user) return;
    try {
      await setVerifiedBadge({ variables: { userId, verified: !user.verifiedBadge } });
      notify(user.verifiedBadge ? 'Badge retiré' : 'Badge attribué', { type: 'success' });
      refetchUser();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const handleRoleChange = async (newRole: string) => {
    try {
      await setUserRole({ variables: { userId, role: newRole } });
      notify('Rôle modifié', { type: 'success' });
      refetchUser();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const handleUpdateUser = async () => {
    try {
      await updateUser({ variables: { userId, input: editForm } });
      notify('Profil mis à jour', { type: 'success' });
      setEditDialogOpen(false);
      refetchUser();
    } catch (err: any) {
      notify(`Erreur: ${err.message}`, { type: 'error' });
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userError || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {userError?.message || 'Utilisateur non trouvé'}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Title title={`Utilisateur: ${user.displayName || user.email}`} />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/users')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>
          Détails de l'utilisateur
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - User Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            {/* Avatar & Name */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Avatar
                src={user.avatarUrl || undefined}
                sx={{ width: 100, height: 100, mx: 'auto', mb: 2 }}
              >
                {user.displayName?.charAt(0) || user.email?.charAt(0)}
              </Avatar>
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography variant="h6" fontWeight={600}>
                  {user.displayName || `${user.firstName} ${user.lastName}` || '-'}
                </Typography>
                {user.verifiedBadge && <VerifiedIcon color="primary" fontSize="small" />}
                {user.isPremium && <StarIcon sx={{ color: 'warning.main' }} fontSize="small" />}
              </Box>
              <Typography variant="body2" color="text.secondary">
                @{user.username || user.slug || '-'}
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
                <Chip
                  label={user.accountStatus}
                  size="small"
                  sx={{
                    bgcolor: `${STATUS_COLORS[user.accountStatus]}20`,
                    color: STATUS_COLORS[user.accountStatus],
                    fontWeight: 500,
                  }}
                />
                <Chip label={user.subscription?.plan || 'FREE'} size="small" color="primary" variant="outlined" />
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Info */}
            <InfoItem icon={<EmailIcon fontSize="small" />} label="Email" value={user.email} />
            <InfoItem icon={<BusinessIcon fontSize="small" />} label="Entreprise" value={user.company} />
            <InfoItem icon={<PersonIcon fontSize="small" />} label="Poste" value={user.jobTitle} />
            <InfoItem icon={<LocationOnIcon fontSize="small" />} label="Localisation" value={user.city && user.country ? `${user.city}, ${user.country}` : user.city || user.country} />
            <InfoItem icon={<CalendarTodayIcon fontSize="small" />} label="Inscription" value={formatDate(user.createdAt)} />
            <InfoItem icon={<VisibilityIcon fontSize="small" />} label="Vues du profil" value={String(user.profileViews)} />

            <Divider sx={{ my: 2 }} />

            {/* Role */}
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Rôle</InputLabel>
              <Select
                value={user.role}
                label="Rôle"
                onChange={(e) => handleRoleChange(e.target.value)}
                disabled={settingRole}
              >
                <MenuItem value="USER">Utilisateur</MenuItem>
                <MenuItem value="MODERATOR">Modérateur</MenuItem>
                <MenuItem value="ADMIN">Administrateur</MenuItem>
              </Select>
            </FormControl>

            {/* Actions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
              >
                Modifier le profil
              </Button>
              <Button
                fullWidth
                variant="outlined"
                color={user.verifiedBadge ? 'error' : 'primary'}
                startIcon={<VerifiedIcon />}
                onClick={handleToggleVerified}
                disabled={settingBadge}
              >
                {user.verifiedBadge ? 'Retirer le badge' : 'Attribuer le badge'}
              </Button>
              {user.accountStatus === 'ACTIVE' ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="error"
                  startIcon={<BlockIcon />}
                  onClick={() => setSuspendDialogOpen(true)}
                >
                  Suspendre le compte
                </Button>
              ) : user.accountStatus === 'SUSPENDED' ? (
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  startIcon={<CheckCircleIcon />}
                  onClick={handleReactivate}
                  disabled={reactivating}
                >
                  Réactiver le compte
                </Button>
              ) : null}
              <Button
                fullWidth
                variant="text"
                startIcon={<OpenInNewIcon />}
                onClick={() => window.open(`${PUBLIC_APP_URL}/profile/${user.slug}`, '_blank')}
              >
                Voir le profil public
              </Button>
            </Box>
          </Paper>
        </Grid>

        {/* Right Column - Tabs */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0 }}>
            <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
              <Tab icon={<CreditCardIcon />} iconPosition="start" label="Cartes" />
              <Tab icon={<SubscriptionsIcon />} iconPosition="start" label="Abonnements" />
              <Tab icon={<BarChartIcon />} iconPosition="start" label="Statistiques" />
            </Tabs>

            {/* Cards Tab */}
            <TabPanel value={tabValue} index={0}>
              <Box sx={{ px: 2 }}>
                {cardsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : cards.length === 0 ? (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Aucune carte créée
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Nom</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell>Statut</TableCell>
                          <TableCell>Créée le</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cards.map((card) => (
                          <TableRow key={card.id}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={500}>
                                {card.displayName || card.slug}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip label={CARD_TYPE_LABELS[card.cardType] || card.cardType} size="small" />
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={card.status}
                                size="small"
                                color={card.status === 'ACTIVE' ? 'success' : 'default'}
                              />
                            </TableCell>
                            <TableCell>{formatDate(card.createdAt)}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Voir la carte">
                                <IconButton
                                  size="small"
                                  onClick={() => window.open(`${PUBLIC_APP_URL}/c/${card.slug}`, '_blank')}
                                >
                                  <OpenInNewIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            </TabPanel>

            {/* Subscriptions Tab */}
            <TabPanel value={tabValue} index={1}>
              <Box sx={{ px: 2 }}>
                {subscriptionLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    {/* Current subscription */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                      Abonnement actuel
                    </Typography>
                    <Card sx={{ mb: 3 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="h6">{user.subscription?.plan || 'FREE'}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Statut: {user.subscription?.status || 'ACTIVE'}
                            </Typography>
                          </Box>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => navigate('/subscriptions')}
                          >
                            Gérer
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* History would go here */}
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                      Historique
                    </Typography>
                    <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                      L'historique détaillé des abonnements sera affiché ici.
                    </Typography>
                  </Box>
                )}
              </Box>
            </TabPanel>

            {/* Stats Tab */}
            <TabPanel value={tabValue} index={2}>
              <Box sx={{ px: 2 }}>
                {statsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : stats ? (
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <StatsCard title="Vues du profil" value={stats.profileViews} icon={<VisibilityIcon />} color="#6366F1" />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <StatsCard title="Cartes créées" value={stats.totalCards} icon={<CreditCardIcon />} color="#10B981" />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <StatsCard title="Connexions" value={stats.totalConnections} icon={<PersonIcon />} color="#F59E0B" />
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <StatsCard title="Jours sur EKAA" value={stats.accountAge} icon={<CalendarTodayIcon />} color="#8B5CF6" />
                    </Grid>
                  </Grid>
                ) : (
                  <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    Statistiques non disponibles
                  </Typography>
                )}
              </Box>
            </TabPanel>
          </Paper>
        </Grid>
      </Grid>

      {/* Suspend Dialog */}
      <Dialog open={suspendDialogOpen} onClose={() => setSuspendDialogOpen(false)}>
        <DialogTitle>Suspendre l'utilisateur</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            L'utilisateur <strong>{user.displayName || user.email}</strong> ne pourra plus se connecter à son compte.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Raison (optionnel)"
            value={suspendReason}
            onChange={(e) => setSuspendReason(e.target.value)}
            placeholder="Ex: Violation des conditions d'utilisation..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuspendDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={handleSuspend} disabled={suspending}>
            {suspending ? <CircularProgress size={20} /> : 'Suspendre'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier le profil</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Prénom"
                value={editForm.firstName}
                onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Nom"
                value={editForm.lastName}
                onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nom d'affichage"
                value={editForm.displayName}
                onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editForm.email}
                onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Poste"
                value={editForm.jobTitle}
                onChange={(e) => setEditForm({ ...editForm, jobTitle: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Entreprise"
                value={editForm.company}
                onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Ville"
                value={editForm.city}
                onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Pays"
                value={editForm.country}
                onChange={(e) => setEditForm({ ...editForm, country: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Bio"
                value={editForm.bio}
                onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleUpdateUser} disabled={updating}>
            {updating ? <CircularProgress size={20} /> : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserShow;
